import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ChevronRight, FileText, Radio, ShieldCheck, TrendingUp, Zap } from 'lucide-react';
import { formatInr, formatSignedPercent } from '../../utils/gmpBoard';
import CompanyLogo from '../common/CompanyLogo';

const PHASE_PILL = {
  open: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  closing_today: 'border-amber-200 bg-amber-50 text-amber-700',
  upcoming: 'border-sky-200 bg-sky-50 text-sky-700',
  closed: 'border-slate-200 bg-slate-100 text-slate-600',
  unknown: 'border-slate-200 bg-slate-100 text-slate-600',
};

const phasePillText = (phase, dayNumber) => {
  if (phase === 'open') return `Live Bidding Day ${dayNumber || 1}`;
  if (phase === 'closing_today') return `Closes Today · Day ${dayNumber || 1}`;
  if (phase === 'upcoming') return 'Opening Soon';
  if (phase === 'closed') return 'Bidding Closed';
  return 'Dates Awaited';
};

/**
 * Live order strip, breadcrumb and the issuer card of the single-company
 * subscription page.
 */
export const IssueHero = ({ issue, profile, phase, dayNumber, windowInfo, detailPath, listPath, onApply, onAnchor }) => {
  const isSme = issue.board === 'sme';
  const gmp = issue.gmp?.hasGmp ? issue.gmp : null;
  const canBid = ['open', 'closing_today', 'upcoming'].includes(phase);
  const leadManagers = profile?.leadManagers || [];
  const meta = [
    profile?.issueType,
    leadManagers.length ? `Lead Managers: ${leadManagers.slice(0, 3).join(', ')}${leadManagers.length > 3 ? ` +${leadManagers.length - 3}` : ''}` : null,
  ].filter(Boolean);

  return (
    <div className="space-y-3">
      <div
        className={`flex flex-wrap items-center justify-between gap-2 rounded-xl px-3.5 py-2 text-[11px] font-semibold ${windowInfo.tone === 'live' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 border border-slate-200'
          }`}
      >
        <p className="flex items-center gap-2">
          <Radio className={`w-3.5 h-3.5 ${windowInfo.tone === 'live' ? 'text-emerald-400 animate-pulse' : 'text-slate-400'}`} aria-hidden="true" />
          <span className="uppercase tracking-wider font-extrabold">Live Order Routing</span>
          <span className={windowInfo.tone === 'live' ? 'text-teal-100' : ''}>{windowInfo.text}</span>
        </p>
        <p className={`flex items-center gap-1.5 ${windowInfo.tone === 'live' ? 'text-slate-300' : 'text-slate-500'}`}>
          <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" />
          {canBid || phase === 'closed' ? 'RHP filed' : 'Offer document stage'} · NSE: {issue.symbol}
        </p>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-2">
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-500 font-medium overflow-x-auto whitespace-nowrap scrollbar-none">
          <Link to="/" className="hover:text-[#008080] transition-colors">
            Home
          </Link>
          <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <Link to={listPath} className="hover:text-[#008080] transition-colors">
            {isSme ? 'SME IPO Subscription' : 'IPO Subscription'}
          </Link>
          <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <span>{isSme ? 'SME Offerings' : 'Mainboard Offerings'}</span>
          <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <span className="text-slate-900 font-semibold" aria-current="page">
            {issue.company}
          </span>
        </nav>
        <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-bold">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${PHASE_PILL[phase]}`}>
            {(phase === 'open' || phase === 'closing_today') && (
              <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" aria-hidden="true" />
            )}
            {phasePillText(phase, dayNumber)}
          </span>
          <span className="px-2.5 py-1 rounded-full border border-slate-200 bg-white text-slate-600">Series: {issue.series}</span>
          <span className="px-2.5 py-1 rounded-full border border-slate-200 bg-white text-slate-600">NSE Symbol: {issue.symbol}</span>
        </div>
      </div>

      <section aria-labelledby="issue-heading" className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-4 sm:p-5">
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
          <div className="flex items-start gap-3.5 min-w-0">
            <CompanyLogo company={issue.company} logoUrl={profile?.logoUrl} size="lg" />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 id="issue-heading" className="text-xl sm:text-2xl font-extrabold text-slate-900 tracking-tight leading-tight">
                  {issue.company}
                </h1>
                <span
                  className={`px-2 py-0.5 rounded-md border text-[11px] font-bold ${isSme ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-indigo-200 bg-indigo-50 text-indigo-700'
                    }`}
                >
                  {isSme ? 'SME IPO' : 'Mainboard IPO'}
                </span>
              </div>
              {profile?.tags?.length > 0 && (
                <ul className="mt-1.5 flex flex-wrap gap-1.5">
                  {profile.tags.map((tag) => (
                    <li key={tag} className="px-2 py-0.5 rounded-md bg-slate-100 text-[11px] font-semibold text-slate-600 capitalize">
                      {tag}
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-1.5 text-xs text-slate-500 leading-relaxed">
                {meta.length ? meta.join(' • ') : `Book built issue on ${isSme ? 'NSE Emerge' : 'NSE'} · Series ${issue.series}`}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0">
            {/* {canBid && (
              <button
                type="button"
                onClick={onApply}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#008080] hover:bg-[#006666] text-xs sm:text-sm font-extrabold text-white shadow-sm transition-colors"
              >
                <Zap className="w-4 h-4" />
                Apply via UPI ASBA
              </button>
            )} */}
            {gmp && (
              <Link
                to={gmp.slug ? `/ipo-gmp/${gmp.slug}` : isSme ? '/sme-ipo-gmp' : '/ipo-gmp'}
                className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl border border-emerald-200 bg-emerald-50 text-xs sm:text-sm font-bold text-emerald-700 hover:bg-emerald-100 transition-colors"
              >
                <TrendingUp className="w-4 h-4" />
                GMP {formatInr(gmp.gmp)}
                {gmp.gmpPercent !== null && ` (${formatSignedPercent(gmp.gmpPercent, 1)})`}
              </Link>
            )}
            {detailPath && (
              <Link
                to={detailPath}
                className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-xs sm:text-sm font-bold text-slate-700 hover:border-teal-200 hover:text-[#008080] transition-colors"
              >
                <FileText className="w-4 h-4" />
                IPO Details
              </Link>
            )}
            <button
              type="button"
              onClick={onAnchor}
              className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-xs sm:text-sm font-bold text-slate-700 hover:border-teal-200 hover:text-[#008080] transition-colors"
            >
              <BookOpen className="w-4 h-4" />
              Anchor Book
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IssueHero;
